import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from '@/lib/router-compat';
import { X, Send, AlertTriangle, CheckCircle2, Loader2, Trash2, ExternalLink, LinkIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useFounderStartup, useFounderSubmissions, useModule } from '@/hooks/useFounderAccelerator';
import { useUserProgress } from '@/hooks/useUserProgress';
import { MODULE_TITLES } from '@/lib/accelerator-types';
import { useScoutConversation } from '@/hooks/useScoutConversation';
import { CompanyLogo } from '@/components/CompanyLogo';
import { cn } from '@/lib/utils';


// Midnight Tactical palette (matches approved mockup)
const MIDNIGHT_HEADER = 'bg-gradient-to-br from-[hsl(220,65%,11%)] via-[hsl(220,55%,18%)] to-[hsl(220,50%,25%)]';

// Four-point MavenWERX star emblem, extracted as inline SVG so it stays crisp
// at avatar/button sizes and inherits `currentColor`.
function ScoutStar({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 100 100" className={className} aria-hidden="true">
      <g fill="currentColor">
        <polygon points="50,4 58,50 50,96 42,50" />
        <polygon points="4,50 50,42 96,50 50,58" />
        <polygon points="18,18 52,48 82,82 48,52" opacity="0.85" />
        <polygon points="82,18 52,48 18,82 48,52" opacity="0.85" />
      </g>
    </svg>
  );
}

type ScoutSource = { label: string; url: string };

const SOURCES_RE = /\n*<!--SCOUT_SOURCES:(\[[\s\S]*?\])-->\s*$/;

function extractSources(content: string): { text: string; sources: ScoutSource[] } {
  const m = content.match(SOURCES_RE);
  if (!m) return { text: content, sources: [] };
  try {
    const parsed = JSON.parse(m[1]);
    if (Array.isArray(parsed)) {
      return {
        text: content.replace(SOURCES_RE, '').trim(),
        sources: parsed.filter((s) => s && typeof s.url === 'string' && typeof s.label === 'string').slice(0, 4),
      };
    }
  } catch {
    /* ignore */
  }
  return { text: content, sources: [] };
}

function getViewingWeek(pathname: string): number | null {
  const m = pathname.match(/\/accelerator\/week\/(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

const WELCOME_MESSAGE =
  "Hi, I'm **The Scout** — your MavenWERX guide. Ask me about your current Sprint, the IP Library, Moneyball, LTTO, or anything else on the platform. If something's broken, describe it and I'll flag it to the team.";

export default function TheScout() {
  const { user } = useAuth();
  const { startup } = useFounderStartup();
  const { submissions } = useFounderSubmissions(startup?.id);
  const { progress } = useUserProgress(user?.id, startup?.cohort_id);
  const location = useLocation();
  const viewingWeek = getViewingWeek(location.pathname);
  const activeWeek = viewingWeek ?? startup?.current_week ?? 0;
  const { module } = useModule(activeWeek);

  const {
    messages: persistedMessages,
    loading: historyLoading,
    appendUserMessage,
    appendAssistantMessage,
    clearConversation,
  } = useScoutConversation();

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Display list: persisted history + optional welcome bubble when empty.
  const displayMessages =
    persistedMessages.length === 0
      ? [{ role: 'assistant' as const, content: WELCOME_MESSAGE }]
      : persistedMessages;

  useEffect(() => {
    if (open) setTimeout(() => textareaRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [displayMessages.length, sending]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const buildContext = useCallback(() => {
    const draftResponses =
      submissions?.find((s) => s.week_number === activeWeek)?.text_responses ?? null;
    const prompts: string[] = [];
    const reqFields = (module?.required_text_fields ?? []) as Array<{ label?: string; prompt?: string }>;
    reqFields.forEach((f) => {
      if (f?.prompt) prompts.push(f.prompt);
      else if (f?.label) prompts.push(f.label);
    });
    return {
      companyName: startup?.scout_card?.company_name ?? null,
      currentWeek: startup?.current_week ?? null,
      completedWeeksCount: progress?.completed_weeks?.length ?? 0,
      completionPercent: startup?.completion_percent ?? null,
      pageUrl: location.pathname,
      viewingWeek: startup ? activeWeek : null,
      moduleTitle: startup ? module?.title || MODULE_TITLES[activeWeek] || null : null,
      moduleBody: startup ? module?.overview_text ?? null : null,
      questionPrompts: prompts,
      draftResponses: draftResponses as Record<string, unknown> | null,
    };
  }, [startup, progress, submissions, module, activeWeek, location.pathname]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    setInput('');
    setSending(true);
    setError(null);

    // Persist user turn immediately.
    await appendUserMessage(text, location.pathname);

    // Assemble outgoing messages from what we have persisted + this new user turn.
    const outgoing = [
      ...persistedMessages.map((m) => ({ role: m.role, content: m.content })),
      { role: 'user' as const, content: text },
    ];

    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('accelerator-assistant', {
        body: {
          messages: outgoing,
          context: buildContext(),
          startupId: startup?.id,
        },
      });

      if (invokeErr) throw invokeErr;
      if (data?.error) throw new Error(data.error);

      const reply: string = data?.persistedContent || data?.reply || "Sorry, I didn't get a response. Please try again.";
      const reported = !!data?.isSystemError && !!data?.reportId;
      await appendAssistantMessage(reply, {
        reported,
        report_id: data?.reportId ?? null,
        page_url: location.pathname,
      });
    } catch (err) {
      console.error('Scout send failed', err);
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSending(false);
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  };

  const handleClear = async () => {
    await clearConversation();
    setError(null);
  };

  // Any signed-in user gets The Scout.
  if (!user) return null;

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open The Scout"
          className={cn(
            'fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg shadow-black/40 ring-2 ring-white/15 transition hover:scale-105 hover:shadow-xl',
            MIDNIGHT_HEADER
          )}
        >
          <ScoutStar className="h-7 w-7" />
        </button>
      )}

      {open && (
        <div
          className={cn(
            'fixed z-50 flex flex-col overflow-hidden rounded-xl border border-[hsl(220,40%,20%)] bg-white shadow-2xl',
            'bottom-0 right-0 left-0 top-auto h-[80vh] sm:bottom-6 sm:right-6 sm:left-auto sm:top-auto sm:h-[600px] sm:w-[400px]'
          )}
        >
          <div className={cn('flex items-center justify-between px-4 py-3 text-white', MIDNIGHT_HEADER)}>
            <div className="flex items-center gap-2.5">
              <ScoutStar className="h-6 w-6 text-white" />
              <div>
                <h3 className="text-sm font-semibold tracking-tight">The Scout</h3>
                <p className="text-[11px] text-white/70">MavenWERX guide — ask anything</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClear}
                disabled={sending || historyLoading || persistedMessages.length === 0}
                className="h-8 w-8 text-white/80 hover:bg-white/10 hover:text-white disabled:opacity-40"
                aria-label="Clear conversation"
                title="Clear conversation"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setOpen(false)}
                className="h-8 w-8 text-white/80 hover:bg-white/10 hover:text-white"
                aria-label="Close The Scout"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
            {historyLoading && persistedMessages.length === 0 && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading conversation…
              </div>
            )}
            {displayMessages.map((m, i) => {
              const { text, sources } = m.role === 'assistant'
                ? extractSources(m.content)
                : { text: m.content, sources: [] as ScoutSource[] };
              const isUser = m.role === 'user';
              return (
                <div
                  key={m.id ?? `msg-${i}`}
                  className={cn('flex items-end gap-2', isUser ? 'justify-end' : 'justify-start')}
                >
                  {!isUser && (
                    <div className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white', MIDNIGHT_HEADER)}>
                      <ScoutStar className="h-4 w-4" />
                    </div>
                  )}
                  <div
                    className={cn(
                      'max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap',
                      isUser ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
                    )}
                  >
                    {text}
                    {sources.length > 0 && (
                      <div className="mt-2 border-t border-border/60 pt-2">
                        <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          <LinkIcon className="h-3 w-3" />
                          Sources
                        </div>
                        <ul className="space-y-1">
                          {sources.map((s, idx) => {
                            const isExternal = /^https?:\/\//i.test(s.url);
                            const key = `${s.url}-${idx}`;
                            const label = (
                              <span className="inline-flex items-center gap-1">
                                <span className="truncate">{s.label}</span>
                                {isExternal && <ExternalLink className="h-3 w-3 shrink-0 opacity-70" />}
                              </span>
                            );
                            return (
                              <li key={key} className="text-xs">
                                {isExternal ? (
                                  <a
                                    href={s.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex max-w-full items-center gap-1 rounded bg-background px-2 py-1 text-primary underline-offset-2 hover:underline"
                                  >
                                    {label}
                                  </a>
                                ) : (
                                  <Link
                                    to={s.url}
                                    onClick={() => setOpen(false)}
                                    className="inline-flex max-w-full items-center gap-1 rounded bg-background px-2 py-1 text-primary underline-offset-2 hover:underline"
                                  >
                                    {label}
                                  </Link>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                    {m.reported && (
                      <div className="mt-2 flex items-center gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900">
                        <AlertTriangle className="h-3 w-3" />
                        <span>Reported to admin team</span>
                        <CheckCircle2 className="ml-auto h-3 w-3" />
                      </div>
                    )}
                  </div>
                  {isUser && (
                    <CompanyLogo
                      logoUrl={startup?.scout_card?.logo_url ?? null}
                      companyName={startup?.scout_card?.company_name || 'You'}
                      size="sm"
                      className="h-7 w-7"
                    />
                  )}
                </div>
              );
            })}
            {sending && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Thinking…
                </div>
              </div>
            )}
            {error && (
              <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            )}
          </div>

          <div className="border-t bg-white p-3 rounded-b-xl">
            <div className="flex items-end gap-2">
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Ask The Scout a question or describe an issue…"
                rows={2}
                disabled={sending}
                className="min-h-[44px] resize-none text-sm"
              />
              <Button
                onClick={sendMessage}
                disabled={sending || !input.trim()}
                size="icon"
                className="h-10 w-10 shrink-0"
                aria-label="Send"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="mt-1.5 text-[10px] text-muted-foreground">
              Press Enter to send · Shift+Enter for newline · History is saved
            </p>
          </div>
        </div>
      )}
    </>
  );
}
